import React, { useCallback, useMemo } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { ClerkProvider, useAuth as useClerkAuth, useClerk, useUser } from '@clerk/clerk-react';

/**
 * Login provider switch for the Auth0 -> Clerk migration.
 *
 * REACT_APP_AUTH_PROVIDER=clerk builds the app against Clerk (needs REACT_APP_CLERK_PUBLISHABLE_KEY);
 * anything else keeps Auth0. Pages call useAppAuth(), which returns the same shape for both
 * providers (the subset of the Auth0 interface the app uses), so they don't care which is active.
 */
export const AUTH_PROVIDER: 'auth0' | 'clerk' = process.env.REACT_APP_AUTH_PROVIDER === 'clerk' ? 'clerk' : 'auth0';

export interface AppUser {
  sub?: string;
  email?: string;
  name?: string;
  picture?: string;
  email_verified?: boolean;
  [key: string]: any;
}

export interface AppAuth {
  isLoading: boolean;
  isAuthenticated: boolean;
  user?: AppUser;
  getAccessTokenSilently: () => Promise<string>;
  loginWithRedirect: (options?: any) => Promise<void>;
  logout: (options?: any) => void | Promise<void>;
  handleRedirectCallback: () => Promise<{ appState?: any }>;
}

export const AppAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (AUTH_PROVIDER === 'clerk') {
    return (
      <ClerkProvider publishableKey={process.env.REACT_APP_CLERK_PUBLISHABLE_KEY!} afterSignOutUrl="/">
        {children}
      </ClerkProvider>
    );
  }
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN!}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID!}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access',
        response_mode: 'query',
      }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      {children}
    </Auth0Provider>
  );
};

const useAuth0Adapter = (): AppAuth => useAuth0() as unknown as AppAuth;

const useClerkAdapter = (): AppAuth => {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  const { user } = useUser();
  const clerk = useClerk();

  const getAccessTokenSilently = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error('Not signed in');
    return token;
  }, [getToken]);

  const loginWithRedirect = useCallback(async (options?: any) => {
    const returnTo: string | undefined = options?.appState?.returnTo;
    await clerk.redirectToSignIn({ signInForceRedirectUrl: `${window.location.origin}${returnTo || '/'}` });
  }, [clerk]);

  const logout = useCallback(
    (options?: any) => clerk.signOut({ redirectUrl: options?.logoutParams?.returnTo || window.location.origin }),
    [clerk]
  );

  // Clerk returns straight to the page that started the sign-in; /callback is only used by Auth0.
  const handleRedirectCallback = useCallback(async () => ({ appState: undefined }), []);

  const appUser = useMemo<AppUser | undefined>(() => user ? {
    // "clerk|" prefix so code that reads the provider from `sub` (as with Auth0's "google-oauth2|...") keeps working
    sub: `clerk|${user.id}`,
    email: user.primaryEmailAddress?.emailAddress,
    name: user.fullName || undefined,
    picture: user.imageUrl,
    email_verified: user.primaryEmailAddress?.verification?.status === 'verified',
  } : undefined, [user]);

  return useMemo(() => ({
    isLoading: !isLoaded,
    isAuthenticated: !!isSignedIn,
    user: appUser,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
    handleRedirectCallback,
  }), [isLoaded, isSignedIn, appUser, getAccessTokenSilently, loginWithRedirect, logout, handleRedirectCallback]);
};

// Chosen once at build time, so the same hooks run on every render.
export const useAppAuth: () => AppAuth = AUTH_PROVIDER === 'clerk' ? useClerkAdapter : useAuth0Adapter;
